import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";

import { refusalText } from "../api/errors";
import { useApiCommand, useApiQuery, useMe } from "../api/hooks";
import type { AgentBlock, ConversationMessage, MessageContext, Mode } from "../api/types";
import { Banner } from "../ui/Banner";
import { Button } from "../ui/Button";
import { EmptyState, Loading } from "../ui/EmptyState";
import { SafeMarkdown } from "../ui/SafeMarkdown";
import { agentUnavailableSentence } from "../ui/text";
import { useAgentStore, useTurnsFor, type StreamPart, type TurnState } from "./agentStore";
import { BlockView } from "./builtinBlocks";
import { conversationQueryKey, isAdminMode, sessionIdOf } from "./conversation";
import { blockType, type BlockContext } from "./registry";

export const MODE_WORDS: Record<Mode, string> = {
  guide: "Guide",
  tutor: "Tutor",
  sme_review: "Review helper",
  explain: "Explain",
  admin: "Admin helper",
  admin_onboarding: "Getting started",
};

interface AgentPanelProps {
  /** `session:<id>`, `sme:<membership id>`, `explain:...`, `tutor:...`, `admin:<person id>` (2.7.1). */
  conversationKey: string;
  mode: Mode;
  /** Where the person is, sent with each message so the agent knows what "this" means. */
  context?: MessageContext;
  title?: string;
  /** Shown while the conversation is empty. */
  emptyHint?: ReactNode;
  className?: string;
}

interface PendingMessage {
  conversationKey: string;
  text: string;
  /** The highest message number the screen knew when the message was sent. */
  afterSeq: number;
}

/** The words of a stored message, for matching it with a message that is still on its way. */
function wordsOf(message: ConversationMessage): string {
  return message.display
    .map((block) => (blockType(block) === "text" ? (block as { text: string }).text : ""))
    .join("")
    .trim();
}

type Group = { kind: "text"; seq: number; text: string } | { kind: "block"; seq: number; block: AgentBlock } | { kind: "tool"; seq: number; part: Extract<StreamPart, { kind: "tool" }> };

/** Put the pieces of a turn in order, joining the words that follow each other. */
function groupParts(turn: TurnState): Group[] {
  const groups: Group[] = [];
  for (const part of turn.parts) {
    if (part.kind === "text") {
      const last = groups[groups.length - 1];
      if (last?.kind === "text") last.text += part.text;
      else groups.push({ kind: "text", seq: part.seq, text: part.text });
    } else if (part.kind === "block") {
      groups.push({ kind: "block", seq: part.seq, block: part.block });
    } else {
      groups.push({ kind: "tool", seq: part.seq, part });
    }
  }
  return groups;
}

const END_SENTENCES: Record<string, string> = {
  cancelled: "You stopped the agent's answer.",
  interrupted: "The agent was interrupted. You can ask again.",
  limit: "The agent reached a limit for one answer. What it did is saved.",
  error: "The agent could not finish its answer.",
};

function ToolLine({ part }: { part: Extract<StreamPart, { kind: "tool" }> }) {
  const word = part.status === "running" ? "Working" : part.status === "refused" ? "Not allowed" : "Done";
  return (
    <li className="agent-tool" data-status={part.status}>
      <span className="agent-tool__status">{word}:</span> {part.summary || part.name}
    </li>
  );
}

function StreamingTurn({ turn, context }: { turn: TurnState; context: BlockContext }) {
  const groups = groupParts(turn);
  const running = turn.status === "running";
  const hasWords = groups.some((g) => g.kind !== "tool");
  return (
    <li className="agent-message" data-role="assistant" data-streaming={running ? "true" : "false"}>
      <span className="sr-only">The agent said</span>
      {groups.map((group) => {
        if (group.kind === "text") {
          return (
            <div key={`t${group.seq}`} className="agent-text" data-agent="true">
              <SafeMarkdown>{group.text}</SafeMarkdown>
            </div>
          );
        }
        if (group.kind === "block") return <BlockView key={`b${group.seq}`} block={group.block} context={{ ...context, turnId: turn.turnId, live: running }} />;
        return (
          <ul key={`k${group.seq}`} className="agent-tools" aria-label="What the agent is doing">
            <ToolLine part={group.part} />
          </ul>
        );
      })}
      {running && !hasWords && (
        <p className="agent-working muted" role="status">
          The agent is working
          <span className="loading__dots" aria-hidden="true" />
        </p>
      )}
    </li>
  );
}

/**
 * The conversation with the agent: the stored messages, the words of a turn as they arrive, the box to
 * write in, and a button to cancel the turn that is running (BUILD-PLAN 2.11.5). Blocks are drawn through the
 * registry (`registerBlockRenderer`). Everything the agent writes is set in the serif type.
 */
export function AgentPanel({ conversationKey, mode, context, title, emptyHint, className }: AgentPanelProps) {
  const queryClient = useQueryClient();
  const admin = isAdminMode(mode);
  const me = useMe();
  const agentStatus = me.data?.agent;
  const available = agentStatus ? agentStatus.available : true;

  const workspaceConversation = useApiQuery("conversation", { key: conversationKey }, { enabled: !admin });
  const adminConversation = useApiQuery("admin_conversation", { key: conversationKey }, { enabled: admin });
  const conversationQuery = admin ? adminConversation : workspaceConversation;
  const conversation = conversationQuery.data;
  const messages = useMemo(() => [...(conversation?.messages ?? [])].sort((a, b) => a.seq - b.seq), [conversation]);

  const turns = useTurnsFor(conversationKey);
  const lastEnd = useAgentStore((state) => state.lastEnd[conversationKey]);
  const running = turns.find((turn) => turn.status === "running");

  const postMessage = useApiCommand("post_message");
  const postAdminMessage = useApiCommand("post_admin_message");
  const cancelWorkspaceTurn = useApiCommand("cancel_turn");
  const cancelAdminTurn = useApiCommand("admin_cancel_turn");
  const cancelTurn = admin ? cancelAdminTurn : cancelWorkspaceTurn;

  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingMessage[]>([]);
  const [queued, setQueued] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  // The page was opened while a turn was running: join it.
  const runningTurn = conversation?.running_turn;
  useEffect(() => {
    if (runningTurn) useAgentStore.getState().adopt(runningTurn.turn_id, conversationKey, runningTurn.text_so_far, runningTurn.last_seq ?? 0);
  }, [runningTurn, conversationKey]);

  // A turn ended: read the stored conversation again, then drop the streamed copy.
  const endedIds = turns.filter((turn) => turn.status === "ended").map((turn) => turn.turnId).join(",");
  useEffect(() => {
    if (!endedIds) return;
    let current = true;
    const ids = endedIds.split(",");
    void queryClient.invalidateQueries({ queryKey: conversationQueryKey(conversationKey, admin) }).then(() => {
      if (!current) return;
      for (const id of ids) useAgentStore.getState().dismiss(id);
    });
    return () => {
      current = false;
    };
  }, [endedIds, conversationKey, admin, queryClient]);

  const lastSeq = messages.length ? messages[messages.length - 1]!.seq : 0;
  const stillWaiting = pending.filter(
    (item) => item.conversationKey === conversationKey && !messages.some((m) => m.role === "user" && m.seq > item.afterSeq && wordsOf(m) === item.text),
  );

  const blockContext = (role: string, turnId?: string | null): BlockContext => ({
    conversationKey,
    mode,
    role,
    turnId,
    live: false,
    sessionId: sessionIdOf(conversationKey),
    admin,
  });

  // Keep the newest words in view inside the panel's own body. The page itself must never move (UI-05):
  // scrollIntoView would scroll every ancestor, so opening a screen with a panel on it would land half-way down.
  const sizeKey = `${messages.length}:${stillWaiting.length}:${turns.map((t) => `${t.turnId}${t.parts.length}${t.status}`).join(",")}`;
  useEffect(() => {
    const body = bodyRef.current;
    if (body) body.scrollTop = body.scrollHeight;
  }, [sizeKey]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending || !available) return;
    setSending(true);
    setProblem(null);
    useAgentStore.getState().clearEnd(conversationKey);
    try {
      const input = { key: conversationKey, text, ...(context ? { context } : {}) };
      const reply = admin ? await postAdminMessage.mutateAsync(input) : await postMessage.mutateAsync(input);
      setDraft("");
      setPending((previous) => [...previous, { conversationKey, text, afterSeq: lastSeq }]);
      setQueued(reply.data?.queued === true);
      void queryClient.invalidateQueries({ queryKey: conversationQueryKey(conversationKey, admin) });
    } catch (error) {
      setProblem(refusalText(error));
    } finally {
      setSending(false);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void send();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send();
    }
  };

  const cancel = () => {
    if (!running) return;
    const input = { key: conversationKey, turn_id: running.turnId };
    if (admin) cancelAdminTurn.mutate(input);
    else cancelWorkspaceTurn.mutate(input);
  };

  const empty = messages.length === 0 && stillWaiting.length === 0 && turns.length === 0;
  const endSentence = lastEnd ? (lastEnd.message || END_SENTENCES[lastEnd.reason]) : null;

  return (
    <section className={clsx("agent-panel", className)} aria-label={title ?? `Agent: ${MODE_WORDS[mode]}`} data-mode={mode}>
      <header className="agent-panel__head">
        <h2 className="agent-panel__title">{title ?? MODE_WORDS[mode]}</h2>
        {available && agentStatus?.demo && <span className="agent-panel__demo muted">Recorded examples</span>}
      </header>

      {!available && <Banner tone="warning">{agentUnavailableSentence(agentStatus?.reason)}</Banner>}

      <div className="agent-panel__body" ref={bodyRef}>
        {conversationQuery.isPending && !conversation && <Loading>Reading the conversation</Loading>}
        {conversationQuery.isError && (
          <p role="alert" className="form-error">
            {refusalText(conversationQuery.error)}{" "}
            <Button variant="quiet" onClick={() => void conversationQuery.refetch()}>
              Try again
            </Button>
          </p>
        )}
        {empty && !conversationQuery.isPending && !conversationQuery.isError && (
          <EmptyState title="No messages yet">{emptyHint ?? "Write to the agent below, or wait for it to begin."}</EmptyState>
        )}
        <ol className="agent-log" role="log" aria-live="polite" aria-label="Conversation with the agent">
          {messages
            .filter((message) => message.display.length > 0)
            .map((message) => (
              <li key={message.seq} className="agent-message" data-role={message.role}>
                <span className="sr-only">{message.role === "user" ? "You said" : "The agent said"}</span>
                {message.display.map((block, index) => (
                  <BlockView key={index} block={block} context={blockContext(message.role, message.turn_id)} />
                ))}
              </li>
            ))}
          {stillWaiting.map((item, index) => (
            <li key={`pending-${index}`} className="agent-message" data-role="user" data-pending="true">
              <span className="sr-only">You said</span>
              <p className="agent-user-text">{item.text}</p>
            </li>
          ))}
          {turns.map((turn) => (
            <StreamingTurn key={turn.turnId} turn={turn} context={blockContext("assistant", turn.turnId)} />
          ))}
        </ol>
        {queued && running && (
          <p className="muted" role="status">
            Your message will be answered when the agent has finished this answer.
          </p>
        )}
        {endSentence && !running && (
          <Banner tone="info" className="agent-panel__end">
            {endSentence}
          </Banner>
        )}
      </div>

      <form className="agent-panel__form" onSubmit={onSubmit}>
        <label htmlFor={`agent-input-${conversationKey}`} className="sr-only">
          Message to the agent
        </label>
        <textarea
          id={`agent-input-${conversationKey}`}
          className="agent-panel__input"
          rows={2}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          disabled={!available}
          placeholder={available ? "Write to the agent" : "The agent is unavailable"}
        />
        <div className="agent-panel__actions row">
          {running && (
            <Button onClick={cancel} aria-label="Cancel the agent's answer" busy={cancelTurn.isPending}>
              Cancel
            </Button>
          )}
          <Button type="submit" variant="primary" busy={sending} disabled={!available || !draft.trim()}>
            Send
          </Button>
        </div>
        {problem && (
          <p role="alert" className="form-error">
            {problem}
          </p>
        )}
        {cancelTurn.isError && (
          <p role="alert" className="form-error">
            {refusalText(cancelTurn.error)}
          </p>
        )}
      </form>
    </section>
  );
}
